import { useState, useEffect, useRef, useCallback, type FormEvent } from "react";
import { useCohort } from "@/hooks/use-cohort";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "wouter";
import {
  Bot, Mail, FolderSearch, Sparkles, CheckCircle2, AlertTriangle,
  Loader2, FileText, Building2, Clock, Brain, MessageCircle, Trash2,
} from "lucide-react";
import InfoTip from "@/components/info-tip";
import { useToast } from "@/hooks/use-toast";

const BASE = import.meta.env.BASE_URL;

interface Intake {
  id: number;
  cohortId: number;
  agentType: string;
  status: string;
  companyName: string | null;
  founderName: string | null;
  founderEmail: string | null;
  driveFolderUrl: string | null;
  extractedData: any;
  aiSummary: string | null;
  createdCompanyId: number | null;
  createdAt: string;
  documents?: any[];
  messages?: any[];
  extractionStats?: { totalDocs: number; readableDocs: number; unreadableDocs: number };
}

const ACTIVE_STATUSES = new Set(["importing", "parsing"]);

const ANALYST_STEPS = ["Create", "Import", "Files Ready", "AI Analysis", "Review", "Done"];

interface AgentNarration {
  avatar: string;
  name: string;
  message: string;
  mood: "neutral" | "working" | "happy" | "error";
  step: number;
  progressPct: number;
}

function getAnalystNarration(intake: Intake): AgentNarration {
  const company = intake.companyName || "this company";
  const base = { avatar: "M", name: "Meera" };

  switch (intake.status) {
    case "draft":
      return { ...base, message: `Hey! I'm ready to dig into ${company}'s documents. Hit Import and I'll pull everything from Google Drive for you.`, mood: "neutral", step: 0, progressPct: 0 };
    case "importing":
      return { ...base, message: `On it! I'm pulling files from Google Drive right now. Hang tight, this usually takes a few seconds...`, mood: "working", step: 1, progressPct: 20 };
    case "imported":
      return { ...base, message: `Got the files! I found documents in the folder. Ready to read through them and extract the key data. Hit Analyze when you're ready.`, mood: "happy", step: 2, progressPct: 40 };
    case "parsing":
      return { ...base, message: `Reading through the documents now... I'm extracting company details, founder info, traction metrics, and funding data. Almost there!`, mood: "working", step: 3, progressPct: 60 };
    case "parsed":
      return { ...base, message: `✅ Analysis complete! I've extracted the company details, founder info, traction metrics, and funding data from the documents. Review my findings below and hit "Create Company" when you're ready.`, mood: "happy", step: 4, progressPct: 80 };
    case "completed":
      return { ...base, message: `${company} is now in your portfolio! All the data has been organized and is ready for your team.`, mood: "happy", step: 5, progressPct: 100 };
    case "import_failed":
      return { ...base, message: `Hmm, I couldn't access that Google Drive folder. Could you double-check that the folder is shared with the connected Google account? I'll try again when you're ready.`, mood: "error", step: 1, progressPct: 15 };
    case "parse_failed":
      return { ...base, message: `I had trouble making sense of the documents. They might be in a format I can't read yet. Want me to try again?`, mood: "error", step: 3, progressPct: 55 };
    default:
      return { ...base, message: `Standing by.`, mood: "neutral", step: 0, progressPct: 0 };
  }
}

function getScoutNarration(intake: Intake): AgentNarration {
  const founder = intake.founderName || "the founder";
  const base = { avatar: "K", name: "Kavya" };

  switch (intake.status) {
    case "draft":
      return { ...base, message: `I'll draft a personalized outreach email for ${founder}. One moment...`, mood: "working", step: 1, progressPct: 30 };
    case "outreach_drafted":
      return { ...base, message: `Done! I've written a warm, professional outreach email to ${founder}. Take a look — once you connect Resend, I'll send it automatically.`, mood: "happy", step: 2, progressPct: 100 };
    default:
      return { ...base, message: `Ready to reach out to ${founder}.`, mood: "neutral", step: 0, progressPct: 0 };
  }
}

function AgentMessage({ narration, isActive }: { narration: AgentNarration; isActive: boolean }) {
  const moodColors = {
    neutral: "border-l-gray-300",
    working: "border-l-blue-400",
    happy: "border-l-emerald-400",
    error: "border-l-red-400",
  };
  const avatarColors = {
    neutral: "bg-gray-200 text-gray-600",
    working: "bg-blue-100 text-blue-600",
    happy: "bg-emerald-100 text-emerald-600",
    error: "bg-red-100 text-red-600",
  };

  return (
    <div className={`mt-3 flex items-start gap-3 p-3 rounded-lg bg-muted/40 border-l-[3px] ${moodColors[narration.mood]}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${avatarColors[narration.mood]}`}>
        {isActive ? <Loader2 className="w-4 h-4 animate-spin" /> : narration.avatar}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-xs font-semibold">{narration.name}</span>
          <span className="text-[10px] text-muted-foreground font-mono uppercase">AI Agent</span>
          {isActive && <span className="text-[10px] text-blue-600 font-medium animate-pulse">working...</span>}
        </div>
        <p className="text-sm text-foreground/80 leading-relaxed">{narration.message}</p>
      </div>
    </div>
  );
}

export default function IntakePipeline({ cohortSlug }: { cohortSlug: string }) {
  const { cohortId } = useCohort(cohortSlug);
  const { toast } = useToast();
  const [intakes, setIntakes] = useState<Intake[]>([]);
  const [loading, setLoading] = useState(true);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<"scout" | "analyst" | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());
  const [detailIntake, setDetailIntake] = useState<Intake | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchIntakes = useCallback(async () => {
    if (!cohortId) return;
    try {
      const res = await fetch(`${BASE}api/intakes?cohortId=${cohortId}`);
      if (res.ok) {
        const data: Intake[] = await res.json();
        setIntakes(data);
        setLoading(false);
      }
    } catch {}
  }, [cohortId]);

  useEffect(() => { if (cohortId) fetchIntakes(); }, [cohortId, fetchIntakes]);

  useEffect(() => {
    const hasActive = intakes.some(i => ACTIVE_STATUSES.has(i.status) || processingIds.has(i.id));
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(fetchIntakes, 3000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [intakes, processingIds, fetchIntakes]);

  const addProcessing = (id: number) => setProcessingIds(prev => new Set(prev).add(id));
  const removeProcessing = (id: number) => setProcessingIds(prev => { const next = new Set(prev); next.delete(id); return next; });

  const handleCreateScout = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const res = await fetch(`${BASE}api/intakes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        cohortId,
        agentType: "scout",
        founderName: form.get("founderName"),
        founderEmail: form.get("founderEmail"),
        companyName: form.get("companyName") || null,
      }),
    });
    if (res.ok) {
      const intake = await res.json();
      setSelectedAgent(null);
      setAgentDialogOpen(false);
      addProcessing(intake.id);
      await fetchIntakes();

      const outreachRes = await fetch(`${BASE}api/intakes/${intake.id}/send-outreach`, { method: "POST" });
      removeProcessing(intake.id);
      if (outreachRes.ok) {
        toast({ title: "Kavya drafted the outreach email!" });
      }
      fetchIntakes();
    }
  };

  const handleCreateAnalyst = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const res = await fetch(`${BASE}api/intakes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        cohortId,
        agentType: "analyst",
        companyName: form.get("companyName") || null,
        driveFolderUrl: form.get("driveFolderUrl") || null,
      }),
    });
    if (res.ok) {
      const intake = await res.json();
      setSelectedAgent(null);
      setAgentDialogOpen(false);
      await fetchIntakes();

      if (intake.driveFolderId) {
        await handleImportAndParse(intake.id);
      }
    }
  };

  const handleImportAndParse = async (intakeId: number) => {
    addProcessing(intakeId);

    const importRes = await fetch(`${BASE}api/intakes/${intakeId}/import-drive`, { method: "POST" });
    if (!importRes.ok) {
      removeProcessing(intakeId);
      fetchIntakes();
      return;
    }
    await fetchIntakes();

    const parseRes = await fetch(`${BASE}api/intakes/${intakeId}/parse`, { method: "POST" });
    removeProcessing(intakeId);
    if (parseRes.ok) {
      toast({ title: "Meera finished the analysis!" });
    } else {
      const body = await parseRes.json().catch(() => ({}));
      toast({
        title: "Meera couldn't analyse these documents",
        description: body.error || `Server returned ${parseRes.status}`,
        variant: "destructive",
      });
    }
    fetchIntakes();
  };

  const handleConfirm = async (intakeId: number) => {
    addProcessing(intakeId);
    const res = await fetch(`${BASE}api/intakes/${intakeId}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    removeProcessing(intakeId);
    if (res.ok) {
      toast({ title: "Company created!", description: "Added to this cohort's portfolio." });
      fetchIntakes();
    } else {
      toast({ title: "Failed to create company", variant: "destructive" });
    }
  };

  const handleDeleteIntake = async (intakeId: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will permanently remove the intake and all its documents.`)) return;
    const res = await fetch(`${BASE}api/intakes/${intakeId}`, { method: "DELETE" });
    if (res.ok) {
      toast({ title: "Intake deleted" });
      fetchIntakes();
    } else {
      toast({ title: "Failed to delete intake", variant: "destructive" });
    }
  };

  const loadDetail = async (intakeId: number) => {
    const res = await fetch(`${BASE}api/intakes/${intakeId}`);
    if (res.ok) {
      const data = await res.json();
      setDetailIntake(data);
    }
  };

  return (
    <div className="space-y-6" data-testid="page-intake-pipeline">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-serif font-bold tracking-tight">Company Intake</h1>
            <InfoTip content="Two AI agents help you add companies: Kavya sends outreach emails to founders, and Meera (Analyst) imports and parses existing deal repositories from Google Drive." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">AI-powered company onboarding pipeline</p>
        </div>
        <Dialog open={agentDialogOpen} onOpenChange={(o) => { setAgentDialogOpen(o); if (!o) setSelectedAgent(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="button-new-intake"><Bot className="w-4 h-4 mr-2" />New Intake</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="font-serif">Choose Your Agent</DialogTitle>
            </DialogHeader>

            {!selectedAgent ? (
              <div className="grid grid-cols-2 gap-4 py-4">
                <Card
                  className="cursor-pointer hover:border-primary/50 transition-colors group"
                  onClick={() => setSelectedAgent("scout")}
                  data-testid="card-agent-scout"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl font-bold text-blue-600">K</span>
                    </div>
                    <h3 className="font-serif font-bold text-lg mb-0.5">Kavya</h3>
                    <p className="text-xs text-muted-foreground italic mb-2">"I'll reach out to founders for you."</p>
                    <p className="text-sm text-muted-foreground">Drafts personalized outreach emails and tracks founder engagement.</p>
                    <Badge className="mt-3 bg-blue-50 text-blue-700 border-blue-200" variant="outline">Email Agent</Badge>
                  </CardContent>
                </Card>

                <Card
                  className="cursor-pointer hover:border-primary/50 transition-colors group"
                  onClick={() => setSelectedAgent("analyst")}
                  data-testid="card-agent-analyst"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl font-bold text-emerald-600">M</span>
                    </div>
                    <h3 className="font-serif font-bold text-lg mb-0.5">Meera</h3>
                    <p className="text-xs text-muted-foreground italic mb-2">"Point me to a Drive folder and I'll handle the rest."</p>
                    <p className="text-sm text-muted-foreground">Imports deal documents, reads through them with AI, and extracts structured company data.</p>
                    <Badge className="mt-3 bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">Analyst Agent</Badge>
                  </CardContent>
                </Card>
              </div>
            ) : selectedAgent === "scout" ? (
              <form onSubmit={handleCreateScout} className="space-y-4 py-2">
                <div className="flex items-center gap-3 mb-2 p-3 bg-blue-50/50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-600">K</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Kavya</p>
                    <p className="text-xs text-muted-foreground">"Give me a name and email, and I'll write a warm intro. I'll ping you once the email is drafted."</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Founder Name</Label>
                    <Input name="founderName" required placeholder="e.g. Rahul Gupta" data-testid="input-scout-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Founder Email</Label>
                    <Input name="founderEmail" type="email" required placeholder="rahul@startup.com" data-testid="input-scout-email" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Company Name (optional)</Label>
                  <Input name="companyName" placeholder="If known" data-testid="input-scout-company" />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setSelectedAgent(null)}>Back</Button>
                  <Button type="submit" className="flex-1" data-testid="button-launch-scout">
                    <Mail className="w-4 h-4 mr-2" /> Send Kavya
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleCreateAnalyst} className="space-y-4 py-2">
                <div className="flex items-center gap-3 mb-2 p-3 bg-emerald-50/50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-emerald-600">M</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Meera</p>
                    <p className="text-xs text-muted-foreground">"Share the Google Drive folder and I'll pull every document, read through them, and have a structured profile ready for you."</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Company Name (optional)</Label>
                  <Input name="companyName" placeholder="e.g. Adaia Ventures" data-testid="input-analyst-company" />
                </div>
                <div className="space-y-2">
                  <Label>Google Drive Folder URL</Label>
                  <Input name="driveFolderUrl" placeholder="https://drive.google.com/drive/folders/..." data-testid="input-analyst-drive" />
                  <p className="text-xs text-muted-foreground">Paste the shared folder URL containing pitch deck, startup data, DD docs</p>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setSelectedAgent(null)}>Back</Button>
                  <Button type="submit" className="flex-1" data-testid="button-launch-analyst">
                    <Sparkles className="w-4 h-4 mr-2" /> Launch Meera
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : intakes.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="flex justify-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-xl font-bold text-blue-600">K</span>
              </div>
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <span className="text-xl font-bold text-emerald-600">M</span>
              </div>
            </div>
            <h3 className="text-lg font-serif font-bold mb-1">Meet your AI team</h3>
            <p className="text-sm text-muted-foreground mb-1"><strong>Kavya</strong> reaches out to founders. <strong>Meera</strong> analyzes deal documents.</p>
            <p className="text-sm text-muted-foreground mb-4">They'll handle the heavy lifting so you can focus on decisions.</p>
            <Button onClick={() => setAgentDialogOpen(true)}>
              <Bot className="w-4 h-4 mr-2" /> Start First Intake
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {intakes.map((intake) => {
            const isActive = processingIds.has(intake.id) || ACTIVE_STATUSES.has(intake.status);
            const isAnalyst = intake.agentType === "analyst";
            const narration = isAnalyst ? getAnalystNarration(intake) : getScoutNarration(intake);

            return (
              <Card key={intake.id} className="transition-shadow hover:shadow-sm" data-testid={`card-intake-${intake.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isAnalyst ? "bg-emerald-100" : "bg-blue-100"
                      }`}>
                        <span className={`text-sm font-bold ${isAnalyst ? "text-emerald-600" : "text-blue-600"}`}>
                          {isAnalyst ? "M" : "K"}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{intake.companyName || intake.founderName || "Unnamed"}</h3>
                          <Badge variant="outline" className="text-[10px] uppercase">{isAnalyst ? "meera" : "kavya"}</Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {intake.founderEmail && <span className="text-xs text-muted-foreground">{intake.founderEmail}</span>}
                          {intake.driveFolderUrl && <span className="text-xs text-muted-foreground">Drive folder linked</span>}
                          <span className="text-xs text-muted-foreground/50">{new Date(intake.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isAnalyst && (intake.status === "draft" || intake.status === "import_failed") && intake.driveFolderUrl && !isActive && (
                        <Button size="sm" variant="outline" onClick={() => handleImportAndParse(intake.id)}>
                          <FolderSearch className="w-3 h-3 mr-1" /> {intake.status === "import_failed" ? "Retry" : "Import"}
                        </Button>
                      )}

                      {(intake.status === "imported" || intake.status === "parse_failed") && !isActive && (
                        <Button size="sm" variant="outline" onClick={async () => {
                          addProcessing(intake.id);
                          try {
                            const res = await fetch(`${BASE}api/intakes/${intake.id}/parse`, { method: "POST" });
                            if (res.ok) {
                              toast({ title: "Meera finished the analysis!" });
                            } else {
                              const body = await res.json().catch(() => ({}));
                              toast({
                                title: intake.status === "parse_failed" ? "Re-analysis still failed" : "Meera couldn't analyse these documents",
                                description: body.error || `Server returned ${res.status}`,
                                variant: "destructive",
                              });
                            }
                          } catch (err: any) {
                            toast({ title: "Analyze failed", description: err.message, variant: "destructive" });
                          } finally {
                            removeProcessing(intake.id);
                            fetchIntakes();
                          }
                        }}>
                          <Brain className="w-3 h-3 mr-1" /> {intake.status === "parse_failed" ? "Retry Analyze" : "Analyze"}
                        </Button>
                      )}

                      {intake.status === "parse_failed" && !isActive && (
                        <Button size="sm" variant="outline" onClick={() => loadDetail(intake.id)}>
                          Review Docs
                        </Button>
                      )}

                      {intake.status === "parsed" && !isActive && (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] font-medium gap-1 animate-in fade-in">
                            <CheckCircle2 className="w-3 h-3" /> Analysis Complete
                          </Badge>
                          <Button size="sm" variant="outline" onClick={() => loadDetail(intake.id)}>
                            Review
                          </Button>
                          <Button size="sm" className="bg-primary hover:bg-primary/90 shadow-sm" onClick={() => handleConfirm(intake.id)}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Create Company
                          </Button>
                        </div>
                      )}

                      {intake.status === "completed" && intake.createdCompanyId && (
                        <Link href={`/c/${cohortSlug}/companies/${intake.createdCompanyId}`}>
                          <Button size="sm" variant="outline">
                            <Building2 className="w-3 h-3 mr-1" /> View Company
                          </Button>
                        </Link>
                      )}

                      {intake.status === "outreach_drafted" && (
                        <Button size="sm" variant="outline" onClick={() => loadDetail(intake.id)}>
                          View Email
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground/50 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteIntake(intake.id, intake.companyName || intake.founderName || "Unnamed")}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  <AgentMessage narration={narration} isActive={isActive} />

                  {isAnalyst && intake.status !== "draft" && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          {ANALYST_STEPS.map((step, idx) => {
                            const isCompleted = idx < narration.step;
                            const isCurrent = idx === narration.step;
                            return (
                              <span
                                key={step}
                                className={`text-[9px] uppercase tracking-wider flex items-center gap-0.5 ${
                                  isCompleted ? "text-emerald-500 font-medium" :
                                  isCurrent ? "text-primary font-semibold" :
                                  "text-muted-foreground/40"
                                }`}
                              >
                                {idx > 0 && <span className="text-muted-foreground/20 mr-1.5">›</span>}
                                {isCompleted && <CheckCircle2 className="w-2.5 h-2.5" />}
                                {step}
                              </span>
                            );
                          })}
                        </div>
                        <span className="text-xs font-mono text-muted-foreground">{narration.progressPct}%</span>
                      </div>
                      <Progress value={narration.progressPct} className="h-1.5" />
                    </div>
                  )}

                  {intake.aiSummary && intake.status === "parsed" && (
                    <div className="mt-3 p-3 bg-muted/50 rounded-md border">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Meera's Analysis</p>
                      <p className="text-sm">{intake.aiSummary}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!detailIntake} onOpenChange={(o) => { if (!o) setDetailIntake(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby={undefined}>
          {detailIntake && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif">{detailIntake.companyName || detailIntake.founderName || "Intake Details"}</DialogTitle>
              </DialogHeader>

              <Tabs defaultValue={detailIntake.agentType === "scout" ? "email" : "data"}>
                <TabsList>
                  {detailIntake.agentType === "scout" && <TabsTrigger value="email">Email</TabsTrigger>}
                  {detailIntake.extractedData && <TabsTrigger value="data">Extracted Data</TabsTrigger>}
                  {detailIntake.documents && detailIntake.documents.length > 0 && <TabsTrigger value="docs">Documents</TabsTrigger>}
                </TabsList>

                <TabsContent value="email" className="space-y-3 mt-4">
                  {detailIntake.messages?.map((msg: any) => (
                    <Card key={msg.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline" className="text-xs">{msg.direction}</Badge>
                          <span className="text-xs text-muted-foreground">{msg.status}</span>
                        </div>
                        <p className="text-sm font-medium mb-1">{msg.subject}</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{msg.body}</p>
                      </CardContent>
                    </Card>
                  ))}
                  {(!detailIntake.messages || detailIntake.messages.length === 0) && (
                    <p className="text-sm text-muted-foreground">No messages yet</p>
                  )}
                </TabsContent>

                <TabsContent value="data" className="mt-4">
                  {detailIntake.extractedData && (
                    <div className="space-y-4">
                      {Object.entries(detailIntake.extractedData).map(([key, value]) => {
                        if (key === "confidence") return null;
                        return (
                          <div key={key} className="border-b pb-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{key.replace(/([A-Z])/g, " $1")}</p>
                            <p className="text-sm mt-0.5">
                              {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
                            </p>
                          </div>
                        );
                      })}
                      {detailIntake.extractedData.confidence !== undefined && (
                        <div className="flex items-center gap-2 pt-2">
                          <span className="text-xs text-muted-foreground">Confidence:</span>
                          <Badge variant="outline">{Math.round(detailIntake.extractedData.confidence * 100)}%</Badge>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="docs" className="mt-4">
                  <div className="space-y-2">
                    {detailIntake.extractionStats && detailIntake.extractionStats.unreadableDocs > 0 && (
                      <div className="p-3 border border-amber-300 bg-amber-50 rounded-md text-sm text-amber-900">
                        <strong>Heads up:</strong> {detailIntake.extractionStats.unreadableDocs} of {detailIntake.extractionStats.totalDocs} files couldn't be read by Meera. Re-upload the flagged ones as Google Docs / Google Slides, or check that the Favcy Drive account has Viewer access, then click <em>Analyze</em> again.
                      </div>
                    )}
                    {detailIntake.documents?.map((doc: any) => (
                      <div key={doc.id} className={`flex items-center justify-between p-3 border rounded-md ${doc.readable === false ? "border-amber-300 bg-amber-50" : ""}`}>
                        <div className="flex items-center gap-2">
                          <FileText className={`w-4 h-4 ${doc.readable === false ? "text-amber-600" : "text-muted-foreground"}`} />
                          <div>
                            <p className="text-sm font-medium">{doc.fileName}</p>
                            <p className="text-xs text-muted-foreground">
                              {doc.category} · {doc.source}
                              {doc.readable === false && <span className="ml-2 text-amber-700 font-medium">· Unreadable</span>}
                              {doc.readable !== false && typeof doc.extractedTextLength === "number" && doc.extractedTextLength > 0 && (
                                <span className="ml-2">· {doc.extractedTextLength.toLocaleString()} chars</span>
                              )}
                            </p>
                          </div>
                        </div>
                        {doc.fileSizeBytes && (
                          <span className="text-xs text-muted-foreground">{(doc.fileSizeBytes / 1024).toFixed(0)} KB</span>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              {detailIntake.status === "parsed" && (
                <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                  <Button variant="outline" onClick={() => setDetailIntake(null)}>Close</Button>
                  <Button onClick={() => { handleConfirm(detailIntake.id); setDetailIntake(null); }}>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Confirm & Create Company
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
